import { useEffect, useState } from 'react';
import { ModalFooter, PaneHeader, Icon, saveBlockers, useToast, writeWithToast, useDirtyDraft } from '../common';
import { api } from '../../lib/api';
import { StaffPreviewOverlay } from './StaffView';
import type { Staff } from './types';

export interface HelperFormProps {
  helper: Staff | null; // null = add a new helper
  onSaved: () => void;
  onClose: () => void;
}

// The helper pane (add + edit). Composes the shared PaneHeader / ModalFooter;
// the fields (name, role, rate, holiday allowance + balance meter) are helper-
// specific. POST/PATCH/DELETE /staff. A helper's leave lives in the rail timeline.
export function HelperForm({ helper, onSaved, onClose }: HelperFormProps) {
  const isNew = !helper;
  const [name, setName] = useState(helper?.name ?? '');
  const [role, setRole] = useState(helper?.role ?? '');
  // A new helper defaults to the minimum wage; an existing one is on it when their
  // stored rate is null. Otherwise they carry a custom rate.
  const [onMinWage, setOnMinWage] = useState(helper ? helper.rate === null : true);
  const [rate, setRate] = useState(helper && helper.rate !== null ? String(helper.rate) : '');
  const [holsTotal, setHolsTotal] = useState(helper ? String(helper.holsTotal) : '28');
  // The login email that lets this person sign in to their own staff self-view.
  const [email, setEmail] = useState(helper?.email ?? '');
  // The mobile the automated staff daily brief is sent to (Story 0). Blank = no
  // number on file yet — nothing downstream can message this person.
  const [phone, setPhone] = useState(helper?.phone ?? '');
  const [minimumWage, setMinimumWage] = useState<number | null>(null);
  // The unsaved-changes guard's one line: the fields a save writes.
  useDirtyDraft('helper', 'this helper', { name, role, onMinWage, rate, holsTotal, email, phone });
  // Admin preview of this staff member's own self-view (staff-self-view).
  const [previewing, setPreviewing] = useState(false);
  const toast = useToast();

  useEffect(() => {
    api<{ minimumWage: number }>('/settings').then((b) => setMinimumWage(b.data.minimumWage)).catch(() => {});
  }, []);

  // Why Save cannot be pressed, in the words to show — see ModalFooter's
  // `blockers`. A greyed button that explains nothing is the defect this replaces.
  const blockers = saveBlockers(name.trim() === '' && 'Give the helper a name.');
  const canSave = blockers.length === 0;
  const used = helper?.holsUsed ?? 0;
  const total = Number(holsTotal) || 0;
  const left = Math.max(0, total - used);
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;

  const save = () => {
    if (!canSave) return;
    const payload = { name: name.trim(), role: role.trim(), rate: onMinWage ? null : rate, holsTotal, email: email.trim(), phone: phone.trim() };
    // Close instantly; the save runs in the background (toast/cache outlive it).
    void writeWithToast(toast, {
      closeFirst: onClose,
      write: () => helper ? api(`/staff/${helper.id}`, { method: 'PATCH', body: payload }) : api('/staff', { method: 'POST', body: payload }),
      success: isNew ? 'Helper added' : 'Helper updated',
      error: 'Could not save the helper.',
      onSaved,
    });
  };
  const doDelete = () => {
    if (!helper) return;
    void writeWithToast(toast, {
      write: () => api(`/staff/${helper.id}`, { method: 'DELETE' }),
      success: 'Helper removed',
      error: 'Could not remove the helper.',
      onSaved,
    });
  };

  return (
    <div className={'pane anim-fade' + (isNew ? ' creating' : '')}>
      <div className="pane-scroll">
        <PaneHeader
          avatar={{ name: isNew ? '?' : helper.name, id: isNew ? 'new' : helper.id }}
          title={isNew ? 'New Helper' : 'Edit Helper'}
          sub={isNew ? 'Add a carer to the rota and leave book' : `${helper.name} · ${helper.role}`}
          isNew={isNew}
        />

        <div className="scard">
          <div className="sh">Details</div>
          <div className="field"><label>Name</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Aliona" autoFocus={isNew} /></div>
          <div className="field"><label>Role</label><input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Carer, Senior carer, Trainer…" /></div>
          <div className="field">
            <label>Login email<span className="field-hint"> · lets them sign in to see their own shifts</span></label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@gmail.com — leave blank for no login" />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Mobile<span className="field-hint"> · where the daily brief is sent · full international number</span></label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+353 87 123 4567 — leave blank if not on file yet" />
          </div>
        </div>

        <div className="scard">
          <div className="sh">Pay &amp; leave</div>
          <div className="field">
            <label>Hourly rate{minimumWage !== null && <span className="field-hint"> · minimum wage €{minimumWage.toFixed(2)}/hr</span>}</label>
            <div className="useg">
              <button type="button" className={onMinWage ? 'on' : ''} onClick={() => setOnMinWage(true)}>Minimum wage</button>
              <button type="button" className={!onMinWage ? 'on' : ''} onClick={() => setOnMinWage(false)}>Custom rate</button>
            </div>
            {!onMinWage && <input type="number" step="0.10" style={{ marginTop: 9 }} value={rate} onChange={(e) => setRate(e.target.value)} placeholder="Custom €/hr" />}
          </div>
          <div className="field" style={{ marginBottom: helper ? 12 : 0 }}><label>Holiday days / yr</label><input type="number" value={holsTotal} onChange={(e) => setHolsTotal(e.target.value)} /></div>
          {helper && (
            <>
              <div className="staff-meter"><div className="fill" style={{ width: `${pct}%` }} /></div>
              <span className="staff-meter-lab"><b>{left}</b> of {total} leave days left · {used} used</span>
            </>
          )}
        </div>

        {helper && (
          <button type="button" className="btn ghost sm staff-preview-btn" onClick={() => setPreviewing(true)}>
            <Icon name="clock" size={14} /> See what they see
          </button>
        )}
      </div>

      {helper && previewing && (
        <StaffPreviewOverlay staffId={helper.id} email={helper.email} onClose={() => setPreviewing(false)} />
      )}

      <ModalFooter
        onCancel={onClose}
        onSave={save}
        saveLabel={isNew ? 'Add helper' : 'Save changes'}
        blockers={blockers}
        danger={helper ? { label: 'Remove helper', confirm: { message: `Remove ${helper.name}? Their upcoming shifts free up; past shifts and leave are kept, and they can be restored.`, confirmLabel: 'Remove helper', onConfirm: () => void doDelete() } } : null}
      />
    </div>
  );
}
